import React, { useState, useEffect } from 'react';
import { FaShoppingCart, FaExternalLinkAlt, FaLink, FaClipboard, FaCheck } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatCurrency } from '../../utils/helpers';

/**
 * Shop Preview component for schools
 * Shows a preview of the school's shop and provides the public link
 */
const ShopPreview = () => {
  const { showToast } = useToast();
  
  // State
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState({});
  const [shopURL, setShopURL] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Fetch shop data on component mount
  useEffect(() => {
    fetchShopData();
  }, []);
  
  // Fetch shop data
  const fetchShopData = async () => {
    try {
      setLoading(true);
      
      // Get school profile info
      const schoolResponse = await api.get('/schools/profile');
      const schoolData = schoolResponse.data.data || {};
      setSchoolInfo(schoolData);
      
      // Get school-specific products
      const productsResponse = await api.get('/ecommerce/products', {
        params: { school: schoolData._id }
      });
      
      setProducts(productsResponse.data.data || []);
      
      // Create the shop URL
      const shopURL = `${window.location.origin}/shop/${schoolData._id}`;
      setShopURL(shopURL);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shop data:', error);
      showToast('Failed to load shop data', 'error');
      setLoading(false);
    }
  };
  
  // Copy shop link to clipboard
  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(shopURL).then(() => {
      setLinkCopied(true);
      showToast('Shop link copied to clipboard', 'success');
      
      // Reset the copied state after 3 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 3000);
    });
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaShoppingCart className="mr-2 text-primary" /> Shop Preview
          </h1>
          <p className="text-gray-600 mt-1">
            Preview your school's online shop
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <a 
            href={shopURL} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-primary flex items-center"
          >
            <FaExternalLinkAlt className="mr-2" /> Visit Shop
          </a>
        </div>
      </div>
      
      {/* Shop Link */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <h2 className="text-xl font-semibold mb-3 flex items-center">
          <FaLink className="mr-2 text-primary" /> Your Shop Link
        </h2>
        <div className="flex items-center">
          <div className="relative flex-1">
            <input
              type="text"
              value={shopURL}
              readOnly
              className="form-input pr-10 w-full bg-gray-50"
            />
            <button
              onClick={copyLinkToClipboard}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-primary"
              title="Copy to clipboard"
            >
              {linkCopied ? <FaCheck /> : <FaClipboard />}
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Share this link with parents and students to access your school's shop.
        </p>
      </div>
      
      {/* Products Preview */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            Shop Preview
          </h2>
          
          {products.length === 0 ? (
            <div className="text-center py-8">
              <FaShoppingCart className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">
                No Products Added Yet
              </h3>
              <p className="text-gray-500 mb-6">
                Add products to your shop to see a preview here
              </p>
              <a
                href="/school/ecommerce"
                className="btn-primary inline-flex items-center"
              >
                Manage Products
              </a>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Here's a preview of how your shop will appear to customers
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.slice(0, 8).map(product => (
                  <div 
                    key={product._id} 
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="relative h-40 bg-gray-100">
                      {product.imageURL ? (
                        <img 
                          src={product.imageURL} 
                          alt={product.name} 
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FaShoppingCart className="text-gray-400 text-4xl" />
                        </div>
                      )}
                      {!product.inStock && (
                        <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                          <span className="text-white font-medium px-3 py-1 bg-red-500 rounded">Out of Stock</span>
                        </div>
                      )}
                      {product.featured && (
                        <div className="absolute top-0 left-0 bg-yellow-500 text-white px-2 py-1 text-xs">
                          Featured
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4">
                      <h3 className="font-medium text-gray-900 mb-1">{product.name}</h3>
                      <div className="text-sm text-gray-500 mb-2">{product.category}</div>
                      <div className="font-bold">{formatCurrency(product.price)}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {products.length > 8 && (
                <div className="text-center mt-6">
                  <p className="text-gray-500">
                    Showing 8 of {products.length} products
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShopPreview; 